import React from "react";
import { useGetFunctionErrorsQuery } from "../function-errors.service";
import { FunctionErrorsTable } from "./FunctionErrorsTable";
import { FunctionErrorsTableSkeleton } from "./FunctionErrorsTableSkeleton";
import { FaExclamationTriangle } from "react-icons/fa";
import { useTheme } from "src/context/theme.context";

type Props = {
  userId?: string;
  limit?: number;
  showUserLink?: boolean;
  messageModalPosition?: "fixed" | "absolute";
};

export const UserFunctionErrorsSection: React.FC<Props> = ({
  userId,
  limit = 100,
  showUserLink = true,
  messageModalPosition = "fixed",
}) => {
  const { isDarkMode } = useTheme();
  const { data: errors, isLoading, isError } = useGetFunctionErrorsQuery({
    where: { userId },
    limit,
  });

  const cardBg = isDarkMode
    ? "bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-gray-700 shadow-xl"
    : "bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-xl";
  const headerBg = isDarkMode
    ? "border-b border-gray-700 bg-gray-800/50"
    : "border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50";
  const titleClass = isDarkMode ? "text-amber-400" : "text-amber-700";
  const mutedClass = isDarkMode ? "text-gray-400" : "text-gray-600";

  if (isError) {
    return (
      <div className={`relative rounded-2xl ${cardBg} overflow-visible`}>
        <div className={`rounded-t-2xl flex items-center gap-3 p-6 sm:p-8 pb-4 ${headerBg}`}>
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isDarkMode ? "bg-amber-500/20" : "bg-amber-100"
            }`}
          >
            <FaExclamationTriangle className={`w-6 h-6 ${titleClass}`} />
          </div>
          <div>
            <h3 className={`text-xl font-bold ${titleClass}`}>Cloud function errors</h3>
            <p className={`text-sm ${mutedClass}`}>
              Failed to load. You may need admin access.
            </p>
          </div>
        </div>
        <div className="p-6 sm:p-8 pt-4" />
      </div>
    );
  }

  return (
    <div className={`relative rounded-2xl ${cardBg} overflow-visible`}>
      <div className={`rounded-t-2xl flex items-center gap-3 p-6 sm:p-8 pb-4 ${headerBg}`}>
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isDarkMode ? "bg-amber-500/20" : "bg-amber-100"
          }`}
        >
          <FaExclamationTriangle className={`w-6 h-6 ${titleClass}`} />
        </div>
        <div>
          <h3 className={`text-xl font-bold ${titleClass}`}>Cloud function errors</h3>
          <p className={`text-sm ${mutedClass}`}>
            Aggregated errors from backend functions (callables, triggers, tasks).
          </p>
        </div>
      </div>
      <div className="p-6 sm:p-8 pt-4">
        {isLoading ? (
          <div className={`overflow-x-auto mb-6 border border-gray-200 divide-y divide-gray-200 rounded-xl overflow-hidden ${isDarkMode ? "border-gray-700 divide-gray-700" : ""}`}>
            <table className="min-w-[700px] w-full text-xs">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="px-4 py-3 text-left uppercase tracking-wider">Function</th>
                  <th className="px-4 py-3 text-left uppercase tracking-wider">Message</th>
                  <th className="px-4 py-3 text-left uppercase tracking-wider">Severity</th>
                  <th className="px-4 py-3 text-center uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-center uppercase tracking-wider">Count</th>
                  <th className="px-4 py-3 text-left uppercase tracking-wider">First / Last seen</th>
                </tr>
              </thead>
              <tbody>
                <FunctionErrorsTableSkeleton rows={8} />
              </tbody>
            </table>
          </div>
        ) : (
          <FunctionErrorsTable
            data={errors ?? []}
            showUserLink={showUserLink}
            messageModalPosition={messageModalPosition}
          />
        )}
      </div>
    </div>
  );
};
